import { randomId } from "../shared/randomId";

import type { ExecutionJobRepository } from "./jobRepository";
import type {
  ExecutionCheckpoint,
  ExecutionJob,
  ExecutionJobEventType,
  ExecutionJobStatus,
} from "./jobTypes";

export class ExecutionJobService {
  constructor(private readonly repository: ExecutionJobRepository) {}

  private async event(
    jobId: string,
    type: ExecutionJobEventType,
    summary: string,
    metadata: Record<string, unknown> = {},
  ) {
    await this.repository.appendEvent({
      id: randomId(),
      executionJobId: jobId,
      type,
      summary,
      metadata,
      createdAt: new Date().toISOString(),
    });
  }

  async enqueue(job: ExecutionJob): Promise<ExecutionJob> {
    const queued: ExecutionJob = {
      ...job,
      status: "QUEUED",
      updatedAt: new Date().toISOString(),
    };

    const created = await this.repository.createJob(queued);

    await this.event(job.id, "JOB_CREATED", "Execution job created.");

    await this.event(
      job.id,
      "JOB_QUEUED",
      "Execution job queued for department worker.",
    );

    return created;
  }

  async claim(
    workerId: string,
    leaseDurationMs = 60_000,
  ): Promise<ExecutionJob | undefined> {
    const job = await this.repository.claimNextJob({
      workerId,
      leaseDurationMs,
    });

    if (!job) {
      return undefined;
    }

    await this.event(
      job.id,
      "JOB_CLAIMED",
      "Execution job claimed by worker.",
      {
        workerId,
        leaseExpiresAt: job.leaseExpiresAt,
      },
    );

    return job;
  }

  async renewLease(
    jobId: string,
    workerId: string,
    leaseDurationMs: number,
  ): Promise<void> {
    await this.repository.renewLease(jobId, workerId, leaseDurationMs);
  }

  async transition(
    jobId: string,
    status: ExecutionJobStatus,
    summary: string,
  ): Promise<ExecutionJob> {
    const job = await this.repository.getJob(jobId);

    if (!job) {
      throw new Error(`Execution job does not exist: ${jobId}`);
    }

    const next: ExecutionJob = {
      ...job,
      status,
      updatedAt: new Date().toISOString(),
    };

    await this.repository.saveJob(next);

    const typeMap: Partial<Record<ExecutionJobStatus, ExecutionJobEventType>> =
      {
        EXECUTING: "EXECUTION_STARTED",
        VERIFYING: "VERIFYING",
        WAITING_APPROVAL: "WAITING_APPROVAL",
        BLOCKED: "BLOCKED",
        FAILED: "FAILED",
        PHASE_GREEN: "PHASE_GREEN",
        CANCELLED: "CANCELLED",
      };

    const type = typeMap[status];

    if (type) {
      await this.event(jobId, type, summary);
    }

    return next;
  }

  async checkpoint(input: {
    jobId: string;
    status: ExecutionJobStatus;
    summary: string;
    evidenceReferences?: string[];
  }): Promise<ExecutionCheckpoint> {
    const job = await this.repository.getJob(input.jobId);

    if (!job) {
      throw new Error(`Execution job does not exist: ${input.jobId}`);
    }

    const checkpoint: ExecutionCheckpoint = {
      id: randomId(),
      executionJobId: job.id,
      status: input.status,
      summary: input.summary,
      evidenceReferences: input.evidenceReferences ?? [],
      attempt: job.attempt,
      createdAt: new Date().toISOString(),
    };

    await this.repository.saveCheckpoint(checkpoint);

    await this.repository.saveJob({
      ...job,
      status: input.status,
      lastCheckpointId: checkpoint.id,
      updatedAt: checkpoint.createdAt,
    });

    await this.event(job.id, "CHECKPOINT_CREATED", input.summary, {
      checkpointId: checkpoint.id,
    });

    return checkpoint;
  }
  async checkpointExternalResourceAndRequeue(input: {
    jobId: string;
    workerId: string;
    reasonCode: string;
    summary: string;
    retryAfterMs: number;
    requiresOwnerAction: boolean;
  }): Promise<ExecutionJob> {
    await this.checkpoint({
      jobId: input.jobId,
      status: "BLOCKED",
      summary: input.summary,
    });

    await this.event(input.jobId, "BLOCKED", input.summary, {
      reasonCode: input.reasonCode,
      retryAfterMs: input.retryAfterMs,
      requiresOwnerAction: input.requiresOwnerAction,
      resumable: true,
    });

    await this.repository.releaseLease(input.jobId, input.workerId);

    const job = await this.repository.getJob(input.jobId);

    if (!job) {
      throw new Error(
        `Execution job does not exist after external-resource checkpoint: ${input.jobId}`,
      );
    }

    const queued: ExecutionJob = {
      ...job,
      status: "QUEUED",
      leaseOwner: undefined,
      leaseExpiresAt: undefined,
      updatedAt: new Date().toISOString(),
    };

    await this.repository.saveJob(queued);

    await this.event(
      input.jobId,
      "JOB_QUEUED",
      "Execution remains resumable after external-resource interruption.",
      {
        reasonCode: input.reasonCode,
        retryAfterMs: input.retryAfterMs,
      },
    );

    return queued;
  }
}
